/**
 * SkyPresets — Named configurations for cloud backdrop atmospheres.
 * Each preset defines a sky gradient and cloud layer parameters.
 *
 * tileScale controls how large the 64px noise tile renders on screen.
 * Close layers get large tileScale (big nearby clouds), distant layers get small tileScale.
 * Different tileScales per layer break tiling alignment so layers complement each other.
 * Opacity ordering: front (closest) layers most opaque, back (distant) fade into sky.
 */
const SkyPresets = {
  overworld: {
    skyGradient: ['#87CEEB', '#4682B4', '#1a1a2e'],
    layers: [
      // Back layer — distant, small-scale clouds
      {
        speed: { x: 2, y: 0.5 },
        noiseScale: 0.003,
        octaves: 3,
        opacity: 0.2,
        parallaxFactor: 0.1,
        coverageThreshold: 0.32,
        edgeSoftness: 0.25,
        evolutionSpeed: 0.05,
        tileScale: 3,
        colorBase: [255, 255, 255],
        colorShadow: [180, 195, 220]
      },
      // Mid layer — medium distance clouds
      {
        speed: { x: 4, y: 1.0 },
        noiseScale: 0.006,
        octaves: 3,
        opacity: 0.35,
        parallaxFactor: 0.25,
        coverageThreshold: 0.38,
        edgeSoftness: 0.22,
        evolutionSpeed: 0.08,
        tileScale: 5,
        colorBase: [245, 248, 255],
        colorShadow: [160, 175, 200]
      },
      // Front layer — closest, big clouds overhead (most opaque)
      {
        speed: { x: 8, y: 2.0 },
        noiseScale: 0.012,
        octaves: 2,
        opacity: 0.55,
        parallaxFactor: 0.5,
        coverageThreshold: 0.42,
        edgeSoftness: 0.2,
        evolutionSpeed: 0.12,
        tileScale: 8,
        colorBase: [230, 240, 255],
        colorShadow: [150, 165, 190]
      }
    ]
  },

  // ── Dungeon themes ──

  // Stone Crypt — slow creeping fog, dank and still
  stone_crypt: {
    skyGradient: ['#0d1a12', '#081210', '#030808'],
    layers: [
      {
        speed: { x: 0.4, y: 0.1 },
        noiseScale: 0.003,
        octaves: 3,
        opacity: 0.15,
        parallaxFactor: 0.05,
        coverageThreshold: 0.2,
        edgeSoftness: 0.3,
        evolutionSpeed: 0.02,
        tileScale: 3,
        colorBase: [50, 75, 55],
        colorShadow: [15, 30, 20]
      },
      {
        speed: { x: 0.8, y: 0.2 },
        noiseScale: 0.006,
        octaves: 2,
        opacity: 0.25,
        parallaxFactor: 0.15,
        coverageThreshold: 0.25,
        edgeSoftness: 0.25,
        evolutionSpeed: 0.03,
        tileScale: 5,
        colorBase: [40, 65, 50],
        colorShadow: [10, 25, 15]
      },
      {
        speed: { x: 1.5, y: 0.4 },
        noiseScale: 0.012,
        octaves: 2,
        opacity: 0.4,
        parallaxFactor: 0.35,
        coverageThreshold: 0.32,
        edgeSoftness: 0.2,
        evolutionSpeed: 0.05,
        tileScale: 8,
        colorBase: [35, 55, 40],
        colorShadow: [8, 20, 12]
      }
    ]
  },

  // Natural Cave — very slow, thick condensation that barely moves
  cave: {
    skyGradient: ['#0a1520', '#061018', '#030a10'],
    layers: [
      {
        speed: { x: 0.15, y: 0.05 },
        noiseScale: 0.002,
        octaves: 3,
        opacity: 0.2,
        parallaxFactor: 0.05,
        coverageThreshold: 0.12,
        edgeSoftness: 0.4,
        evolutionSpeed: 0.012,
        tileScale: 3,
        colorBase: [40, 80, 90],
        colorShadow: [10, 30, 40]
      },
      {
        speed: { x: 0.4, y: 0.1 },
        noiseScale: 0.005,
        octaves: 2,
        opacity: 0.35,
        parallaxFactor: 0.12,
        coverageThreshold: 0.18,
        edgeSoftness: 0.35,
        evolutionSpeed: 0.02,
        tileScale: 5,
        colorBase: [35, 70, 80],
        colorShadow: [8, 25, 35]
      },
      {
        speed: { x: 0.8, y: 0.2 },
        noiseScale: 0.01,
        octaves: 2,
        opacity: 0.5,
        parallaxFactor: 0.3,
        coverageThreshold: 0.25,
        edgeSoftness: 0.3,
        evolutionSpeed: 0.03,
        tileScale: 8,
        colorBase: [30, 60, 70],
        colorShadow: [6, 20, 30]
      }
    ]
  },

  // Ancient Ruins — drifting dust and sand, moderate wind
  ruins: {
    skyGradient: ['#2a1a10', '#1a1008', '#0a0804'],
    layers: [
      {
        speed: { x: 1.5, y: 0.4 },
        noiseScale: 0.003,
        octaves: 3,
        opacity: 0.15,
        parallaxFactor: 0.08,
        coverageThreshold: 0.2,
        edgeSoftness: 0.3,
        evolutionSpeed: 0.05,
        tileScale: 3,
        colorBase: [120, 85, 50],
        colorShadow: [50, 30, 15]
      },
      {
        speed: { x: 3.0, y: 0.8 },
        noiseScale: 0.008,
        octaves: 2,
        opacity: 0.25,
        parallaxFactor: 0.2,
        coverageThreshold: 0.28,
        edgeSoftness: 0.22,
        evolutionSpeed: 0.08,
        tileScale: 5,
        colorBase: [100, 70, 40],
        colorShadow: [40, 25, 12]
      },
      {
        speed: { x: 5.0, y: 1.5 },
        noiseScale: 0.014,
        octaves: 2,
        opacity: 0.4,
        parallaxFactor: 0.4,
        coverageThreshold: 0.35,
        edgeSoftness: 0.18,
        evolutionSpeed: 0.12,
        tileScale: 8,
        colorBase: [90, 60, 35],
        colorShadow: [35, 20, 10]
      }
    ]
  },

  // Fire Temple — fast roiling smoke, turbulent, aggressive morphing
  fire_temple: {
    skyGradient: ['#1a0800', '#120500', '#080200'],
    layers: [
      {
        speed: { x: 4.0, y: 2.0 },
        noiseScale: 0.004,
        octaves: 3,
        opacity: 0.2,
        parallaxFactor: 0.06,
        coverageThreshold: 0.15,
        edgeSoftness: 0.28,
        evolutionSpeed: 0.12,
        tileScale: 3,
        colorBase: [140, 50, 15],
        colorShadow: [60, 15, 5]
      },
      {
        speed: { x: 7.0, y: 3.0 },
        noiseScale: 0.01,
        octaves: 2,
        opacity: 0.35,
        parallaxFactor: 0.18,
        coverageThreshold: 0.2,
        edgeSoftness: 0.2,
        evolutionSpeed: 0.18,
        tileScale: 5,
        colorBase: [180, 70, 10],
        colorShadow: [80, 25, 5]
      },
      {
        speed: { x: 10.0, y: 4.0 },
        noiseScale: 0.016,
        octaves: 2,
        opacity: 0.5,
        parallaxFactor: 0.4,
        coverageThreshold: 0.3,
        edgeSoftness: 0.18,
        evolutionSpeed: 0.25,
        tileScale: 8,
        colorBase: [200, 90, 15],
        colorShadow: [100, 35, 8]
      }
    ]
  },

  // Ice Dungeon — glacial, barely moving, dense crystalline fog
  ice_dungeon: {
    skyGradient: ['#101828', '#0a1020', '#050a15'],
    layers: [
      {
        speed: { x: 0.2, y: 0.08 },
        noiseScale: 0.002,
        octaves: 3,
        opacity: 0.2,
        parallaxFactor: 0.05,
        coverageThreshold: 0.12,
        edgeSoftness: 0.4,
        evolutionSpeed: 0.012,
        tileScale: 3,
        colorBase: [100, 130, 170],
        colorShadow: [30, 50, 80]
      },
      {
        speed: { x: 0.5, y: 0.15 },
        noiseScale: 0.005,
        octaves: 2,
        opacity: 0.35,
        parallaxFactor: 0.12,
        coverageThreshold: 0.18,
        edgeSoftness: 0.35,
        evolutionSpeed: 0.02,
        tileScale: 5,
        colorBase: [90, 120, 160],
        colorShadow: [25, 45, 75]
      },
      {
        speed: { x: 1.0, y: 0.3 },
        noiseScale: 0.01,
        octaves: 2,
        opacity: 0.5,
        parallaxFactor: 0.3,
        coverageThreshold: 0.25,
        edgeSoftness: 0.3,
        evolutionSpeed: 0.03,
        tileScale: 8,
        colorBase: [80, 110, 150],
        colorShadow: [20, 40, 70]
      }
    ]
  },

  // Arcane Library — slowly pulsing magical mist, mysterious
  arcane_library: {
    skyGradient: ['#140a20', '#0e0818', '#080510'],
    layers: [
      {
        speed: { x: 0.3, y: 0.15 },
        noiseScale: 0.003,
        octaves: 3,
        opacity: 0.18,
        parallaxFactor: 0.06,
        coverageThreshold: 0.15,
        edgeSoftness: 0.35,
        evolutionSpeed: 0.035,
        tileScale: 3,
        colorBase: [90, 50, 130],
        colorShadow: [30, 15, 55]
      },
      {
        speed: { x: 0.8, y: 0.3 },
        noiseScale: 0.006,
        octaves: 2,
        opacity: 0.3,
        parallaxFactor: 0.15,
        coverageThreshold: 0.2,
        edgeSoftness: 0.3,
        evolutionSpeed: 0.06,
        tileScale: 5,
        colorBase: [80, 45, 120],
        colorShadow: [25, 12, 50]
      },
      {
        speed: { x: 1.5, y: 0.6 },
        noiseScale: 0.012,
        octaves: 2,
        opacity: 0.45,
        parallaxFactor: 0.35,
        coverageThreshold: 0.28,
        edgeSoftness: 0.25,
        evolutionSpeed: 0.08,
        tileScale: 8,
        colorBase: [70, 38, 110],
        colorShadow: [20, 10, 45]
      }
    ]
  }
};

export default SkyPresets;
